import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type {
  KernelEvent,
  ModeDefinition,
  ReplayMeta,
  RunBundle,
} from "@picnic-field/local-host/protocol";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import type { ReplayInfo } from "../../../lib/api/socialApi";
import { isExpired, useSocialApi } from "../../../lib/api/socialApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { Button } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { PicoBubble } from "../../../components/PicoBubble";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useCourseApi } from "../../../lib/api/courseApi";
import { CourseScene, courseSceneOf } from "../../course";
import { useHost } from "../../course";
import { formatClock } from "../../course";
import { presetTitle } from "../../course";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { homeFor } from "../../../constants/paths";
import { useSession } from "../../../store/session/SessionProvider";
import { track } from "../../../lib/telemetry/telemetry";
import { usePerfTelemetry } from "../../../hooks/useTelemetry";
import type { Speed } from "../lib/logic";
import {
  SPEEDS,
  fraction,
  initialPlay,
  keySeek,
  onReplayEvent,
  showTimes,
  tickAt,
  tickToS,
} from "../lib/logic";
import { RoomReplayView } from "./RoomReplayView";
import { isRoomReplay } from "../lib/roomReplay";
import "../../../styles/course.css";
import "../../../styles/replay.css";
import type { PlayState } from "../types";

/**
 * FS-14-01 리플레이 재생(/replay/:id) — SCR v0.6 sfs-14(교사 판) · 학생 변형(같은 컨트롤 · 해시·엔진 정보 비노출).
 * 재생/일시정지 · 배속(0.5·1·2) · 타임라인 구간 이동 · 재생 방식 상태(입력 재생 / 위치 트랙 · 로컬 PH1 / 서버 재생 · 권위 PH2 — 교사만).
 * FE-501(해시 불일치 · 빌드 다름) → 위치 트랙으로 끊김 없이. 학생 문구 '다시 맞춰서 재생해요'(오류 배너 아님). 만료(410) 상태.
 * FS-14-02(영상)는 반 기록(룸 권위 리플레이 — ./RoomReplayView)에서만 — 로컬 리플레이 교사 화면은 흐린 자리에 그 안내만.
 */
interface Data {
  info: ReplayInfo;
  mode: ModeDefinition;
  bundles: RunBundle[];
  file: Uint8Array;
  track: Uint8Array | null;
  palette: PaletteLike | null;
}

type Load =
  | { status: "loading" }
  | { status: "expired" }
  | { status: "error"; code: ErrorCode }
  | { status: "ready"; data: Data };

declare global {
  interface Window {
    __pfReplay?: {
      state: () => { tick: number; duration: number; speed: number; play: PlayState };
    };
  }
}

/**
 * /replay/:id — 상세를 먼저 읽어 룸 권위 리플레이(room-replay@1 · P3-S3)면 룸 재생기로, 아니면 로컬 리플레이(커널 호스트)로.
 * 상세를 못 읽으면(만료 · 오류) 로컬 화면이 다시 읽어 그 상태 화면을 보인다.
 */
export function ReplayScreen() {
  const { id = "" } = useParams();
  const social = useSocialApi();
  const session = useSession();
  const role = session.status === "ready" ? session.session.role : "student";
  const teacher = role === "teacher" || role === "operator";
  const [pick, setPick] = useState<{ id: string; info: ReplayInfo | null } | null>(null);
  useEffect(() => {
    if (!social) return;
    let alive = true;
    setPick(null);
    social
      .replay(id)
      .then((info) => alive && setPick({ id, info }))
      .catch(() => alive && setPick({ id, info: null }));
    return () => {
      alive = false;
    };
  }, [id, social]);
  if (!pick || pick.id !== id) return <ReplayLoading />;
  if (pick.info && isRoomReplay(pick.info))
    return <RoomReplayView key={id} info={pick.info} teacher={teacher} />;
  return <LocalReplayScreen key={id} id={id} preInfo={pick.info} />;
}

function ReplayLoading() {
  const { t } = useI18n();
  return (
    <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
      <div className="pf-pane" aria-busy="true">
        <PicoBubble text={t("replay.loading")} busy />
      </div>
    </ScreenRoot>
  );
}

function LocalReplayScreen({ id, preInfo }: { id: string; preInfo: ReplayInfo | null }) {
  const { t } = useI18n();
  const { density } = useDensity();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const role = session.status === "ready" ? session.session.role : "student";
  // 운영자(S5 역할)도 교사처럼 본다(정본 표시 · 시간)
  const teacher = role === "teacher" || role === "operator";
  const social = useSocialApi();
  const courseApi = useCourseApi();
  const fieldApi = useFieldApi();

  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const [meta, setMeta] = useState<ReplayMeta | null>(null);
  const [play, setPlay] = useState<PlayState>(initialPlay("input"));
  const [speed, setSpeedState] = useState<Speed>(1);
  const [tick, setTick] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  /** 불러올 수 없는 리플레이(위치 트랙도 없음 — 커널이 FE-501 로 거절) */
  const [unreadable, setUnreadable] = useState(false);
  /** 구간 이동 중(뒤로 가면 커널이 처음부터 다시 돌린다 — 긴 리플레이는 몇 초) */
  const [seeking, setSeeking] = useState<number | null>(null);
  const seekingRef = useRef<number | null>(null);

  const onEvent = useCallback((e: KernelEvent) => setPlay((s) => onReplayEvent(s, e)), []);
  const reported = useRef(false);
  const world = useHost(onEvent);
  const hostReady = world.status !== "creating" && world.status !== "error";

  // ── 데이터 ──
  useEffect(() => {
    if (!social || !courseApi || !fieldApi) return;
    let cancelled = false;
    setLoad({ status: "loading" });
    (async () => {
      // 첫 읽기는 위에서 받은 상세 · '다시 해 보기'(seq)는 새로
      const info = (seq === 0 ? preInfo : null) ?? (await social.replay(id));
      const [modeE, file, track] = await Promise.all([
        courseApi.mode(info.modeId),
        social.replayFile(id),
        social.replayTrack(id),
      ]);
      const bundles = await Promise.all(info.snapshotIds.map((s) => courseApi.runBundle(s)));
      // 작품마다 제 appearance.palette 이름의 팔레트(2026-09-30 팔레트 버전 전환)
      const palette = await fieldApi.palettes(bundles.map((b) => b.appearance?.palette));
      if (!cancelled)
        setLoad({
          status: "ready",
          data: { info, mode: modeE.definition, bundles, file, track, palette },
        });
    })().catch((e) => {
      if (cancelled) return;
      if (isExpired(e)) setLoad({ status: "expired" });
      else setLoad({ status: "error", code: toApiError(e).code });
    });
    return () => {
      cancelled = true;
    };
  }, [id, preInfo, social, courseApi, fieldApi, seq]);

  // ── 커널에 싣기(멈춘 상태) ──
  const data = load.status === "ready" ? load.data : null;
  useEffect(() => {
    if (!data || !hostReady || !courseApi) return;
    let alive = true;
    void (async () => {
      const config = await courseApi.runtimeConfig();
      const r = await world.loadReplay({
        file: data.file,
        positionTrack: data.track,
        bundles: data.bundles,
        mode: data.mode,
        runtimeConfig: config,
      });
      if (!alive) return;
      if (!r) {
        setUnreadable(true);
        return;
      }
      setUnreadable(false);
      reported.current = false;
      setMeta(r.meta);
      setTick(0);
      setSpeedState(1);
      setPlay({ ...initialPlay(r.playback), switched: r.playback === "track" });
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, hostReady]);

  // 틱(표시 — 10Hz 로 줄여서)
  useEffect(() => {
    let last = 0;
    return world.onFrame((f) => {
      const target = seekingRef.current;
      if (target != null && Math.abs(f.tick - target) <= 2) {
        seekingRef.current = null;
        setSeeking(null);
      }
      const now = performance.now();
      if (now - last < 100 && f.tick !== 0) return;
      last = now;
      setTick(f.tick);
    });
  }, [world]);

  // 재생 검증 결과 보고(PATCH …/replays/{id} {hashOk}) — 위치 트랙으로 넘어가면 false, 끝까지 입력 재생이면 true
  useEffect(() => {
    if (!social || reported.current || !meta) return;
    if (play.switched || play.ended) {
      reported.current = true;
      void social.reportHash(id, !play.switched && play.playback === "input");
    }
  }, [social, id, meta, play.switched, play.ended, play.playback]);

  // 계측: 위치 트랙 폴백(한 번) · 재생 중 fps
  useEffect(() => {
    if (play.switched) track({ t: "playback", code: "track", detail: "replay" });
  }, [play.switched]);
  usePerfTelemetry("replay", play.playing);

  const duration = meta?.durationTicks ?? data?.info.meta?.durationTicks ?? 0;
  const host = world.host();

  const doPlay = () => {
    if (!host) return;
    if (play.ended || tick >= duration) host.seek(0);
    host.start();
    setPlay((s) => ({ ...s, playing: true, ended: false }));
  };
  const doPause = () => {
    host?.pause();
    setPlay((s) => ({ ...s, playing: false }));
  };
  const setSpeed = (s: Speed) => {
    host?.setSpeed(s);
    setSpeedState(s);
  };
  const seek = (tk: number) => {
    seekingRef.current = tk;
    setSeeking(tk);
    // 프레임이 오지 않아도(멈춘 호스트가 보내지 않는 경우) 15초 뒤에는 표시를 거둔다
    window.setTimeout(() => {
      if (seekingRef.current !== tk) return;
      seekingRef.current = null;
      setSeeking(null);
    }, 15_000);
    host?.seek(tk);
    setTick(tk);
    setPlay((s) => ({ ...s, ended: tk >= duration && duration > 0 }));
  };

  const info = data?.info ?? null;
  const preset = data?.mode.presets.find((p) => p.key === info?.presetKey);
  const presetName = preset ? presetTitle(t, preset) : "";
  const who = info ? (info.mine && !teacher ? t("replay.mine") : info.nickname) : "";
  useTopBar({
    icon: "🎬",
    title: info ? t("replay.titleOf", { name: who, course: presetName }) : t("replay.title"),
    chips: info
      ? [
          {
            label: t(info.completed ? "run.result.done" : "run.result.notDone"),
            testId: "replay-chip-result",
          },
          ...(teacher
            ? [
                {
                  label: t("replay.teacher.hashChip", {
                    hash: info.hashOk ? "✓" : "✕",
                    mode: t(`replay.mode.${play.playback}`),
                  }),
                  testId: "replay-chip-hash",
                },
              ]
            : []),
        ]
      : [],
  });

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.__pfReplay = { state: () => ({ tick, duration, speed, play }) };
  });

  const home = homeFor(teacher ? "teacher" : "student");
  if (load.status === "expired")
    return (
      <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
        <div className="pf-pane pf-pane--hero" data-testid="replay-expired">
          <PicoBubble text={t("fe.FE-504.student.cause")} live={false} />
          <div className="pf-row pf-row--wrap">
            <Button variant="ghost" icon="🏠" onClick={() => navigate(home)}>
              {t("common.toEntrance")}
            </Button>
          </div>
        </div>
      </ScreenRoot>
    );
  if (load.status === "error")
    return (
      <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
        <FeBanner
          code={load.code}
          audience={teacher ? "teacher" : "student"}
          actions={[{ label: t("common.retry"), icon: "⟲", onClick: () => setSeq((n) => n + 1) }]}
        />
      </ScreenRoot>
    );
  if (unreadable)
    return (
      <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
        <div className="pf-pane pf-pane--hero" data-testid="replay-unreadable">
          <PicoBubble text={t("replay.unreadable")} live={false} />
          {teacher && (
            <p className="pf-muted">
              {`${t("fe.FE-501.teacher.cause")} (${t("common.codeLabel", { code: "FE-501" })})`}
            </p>
          )}
          <div className="pf-row pf-row--wrap">
            <Button variant="ghost" icon="🏠" onClick={() => navigate(home)}>
              {t("common.toEntrance")}
            </Button>
          </div>
        </div>
      </ScreenRoot>
    );
  if (!data || !info)
    return (
      <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
        <div className="pf-pane" aria-busy="true">
          <PicoBubble text={t("replay.loading")} busy />
        </div>
      </ScreenRoot>
    );

  const times = showTimes(teacher ? "teacher" : "student", info.mine);
  const frac = fraction(tick, duration);
  const ready = world.status === "ready" && !!meta;
  const hostError = world.status === "error" && !unreadable;
  const chips: { key: "input" | "track" | "server"; label: string; ph2?: boolean }[] = [
    { key: "input", label: t(teacher ? "replay.state.inputTeacher" : "replay.state.input") },
    { key: "track", label: t(teacher ? "replay.state.trackTeacher" : "replay.state.track") },
    ...(teacher ? [{ key: "server" as const, label: t("replay.state.server"), ph2: true }] : []),
  ];

  return (
    <ScreenRoot
      id="FS-14-01"
      title={t("replay.title")}
      hideTitle
      fill
      className={`pf-replay pf-replay--${density} pf-course--${density}${teacher ? " pf-replay--teacher" : ""}`}
    >
      <div className="pf-replay__main">
        <div className="pf-replay__left">
          {hostError && (
            <FeBanner
              code="NET-SERVER"
              audience={teacher ? "teacher" : "student"}
              actions={[
                { label: t("common.retry"), icon: "⟲", onClick: () => window.location.reload() },
              ]}
            />
          )}
          <CourseScene
            run={world}
            load={world.load}
            bundle={data.bundles[0] ?? null}
            palette={data.palette}
            ghosts={[]}
            label={t("replay.sceneLabel")}
            meLabel={who}
            {...(info ? { courseScene: courseSceneOf(info.presetKey) } : {})}
            orbit
          >
            {times && (
              <div className="pf-hud pf-hud--timer" data-testid="replay-time">
                {t("replay.time", {
                  now: formatClock(tickToS(tick), false),
                  total: formatClock(tickToS(duration), false),
                })}
              </div>
            )}
          </CourseScene>
          <div className="pf-pane pf-replay__controls" data-testid="replay-controls">
            <div className="pf-replay__row">
              <Button
                variant="primary"
                className="pf-replay__btn"
                aria-label={t("replay.play")}
                aria-pressed={play.playing}
                disabled={!ready || play.playing}
                onClick={doPlay}
                data-testid="replay-play"
              >
                ▶
              </Button>
              <Button
                variant="ghost"
                className="pf-replay__btn"
                aria-label={t("replay.pause")}
                disabled={!ready || !play.playing}
                onClick={doPause}
                data-testid="replay-pause"
              >
                ⏸
              </Button>
              <div
                ref={trackRef}
                className="pf-replay__timeline"
                role="slider"
                tabIndex={0}
                aria-label={t("replay.timeline")}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(frac * 100)}
                aria-valuetext={
                  times
                    ? t("replay.time", {
                        now: formatClock(tickToS(tick), false),
                        total: formatClock(tickToS(duration), false),
                      })
                    : t("replay.percent", { n: Math.round(frac * 100) })
                }
                aria-disabled={!ready}
                aria-busy={seeking != null}
                data-testid="replay-timeline"
                onPointerDown={(e) => {
                  if (!ready) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.setPointerCapture?.(e.pointerId);
                  seek(tickAt((e.clientX - r.left) / Math.max(1, r.width), duration));
                }}
                onPointerMove={(e) => {
                  if (!ready || !(e.buttons & 1)) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  seek(tickAt((e.clientX - r.left) / Math.max(1, r.width), duration));
                }}
                onKeyDown={(e) => {
                  if (!ready) return;
                  const n = keySeek(e.key, tick, duration);
                  if (n == null) return;
                  e.preventDefault();
                  seek(n);
                }}
              >
                <span className="pf-replay__bar">
                  <span className="pf-replay__fill" style={{ width: `${frac * 100}%` }} />
                </span>
              </div>
              <div className="pf-replay__speeds" role="radiogroup" aria-label={t("replay.speed")}>
                {SPEEDS.map((s) => (
                  <Button
                    key={s}
                    role="radio"
                    aria-checked={speed === s}
                    variant={speed === s ? "accent" : "ghost"}
                    className="pf-replay__btn"
                    disabled={!ready}
                    onClick={() => setSpeed(s)}
                    data-speed={s}
                  >
                    {`${s}×`}
                  </Button>
                ))}
              </div>
              {/* 나가기(2026-10-09) — 연 곳(코스 결과 · 전시관 · 교사 화면)으로 돌아간다. 주소로 바로 들어왔으면 홈 */}
              <Button
                variant="ghost"
                icon="✕"
                className="pf-replay__exit"
                onClick={() => (location.key !== "default" ? navigate(-1) : navigate(home))}
                data-testid="replay-exit"
              >
                {t("replay.exit")}
              </Button>
            </div>
            <ul
              className="pf-replay__state"
              aria-label={t("replay.stateLabel")}
              data-testid="replay-state"
            >
              {chips.map((c) => (
                <li
                  key={c.key}
                  className={`pf-replay__chip${play.playback === c.key ? " pf-replay__chip--on" : ""}${c.ph2 ? " pf-replay__chip--ph2" : ""}`}
                  data-mode={c.key}
                  aria-current={play.playback === c.key ? "true" : undefined}
                >
                  {c.label}
                </li>
              ))}
            </ul>
            {seeking != null && (
              <p className="pf-muted" role="status" data-testid="replay-seeking">
                {t("replay.seeking")}
              </p>
            )}
            {play.switched && (
              <p className="pf-replay__note" role="status" data-testid="replay-fe501">
                <span aria-hidden="true">🔄 </span>
                {teacher
                  ? `${t("fe.FE-501.teacher.cause")} — ${t("fe.FE-501.teacher.next")} (${t("common.codeLabel", { code: "FE-501" })})`
                  : t("fe.FE-501.student.cause")}
              </p>
            )}
          </div>
        </div>
        {teacher && (
          <aside
            className="pf-pane pf-pane--dim pf-replay__side"
            aria-disabled="true"
            data-testid="replay-ph3"
          >
            <h2 className="pf-h2">{t("replay.teacher.videoTitle")}</h2>
            <p className="pf-muted">{t("replay.teacher.videoPh3")}</p>
          </aside>
        )}
      </div>
    </ScreenRoot>
  );
}
